import { useCallback, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router';

import { Button, Drawer } from '@/design-system';
import { ImpersonationBar } from '@/features/admin';
import { FirstRunTour } from '@/features/tour';
import { BreakpointProvider, useBreakpoint } from '@/shared/lib';
import { ErrorBoundary, ShellTopBarContext, type ShellTopBarContextValue } from '@/shared/ui';

import { NavRail } from './NavRail';
import { RailBrand } from './RailBrand';
import { RailCountsProvider } from './RailCountsProvider';
import { SampleDataNotice } from './SampleDataNotice';

const DRAWER_ID = 'sk-rail-drawer';

/**
 * De schil om elk ingelogd scherm.
 *
 * Links de donkere navigatierail, rechts het werkvlak. Onder de 1000px is er
 * geen ruimte voor de rail: die verdwijnt en komt terug als schuiflade achter de
 * knop "Menu" in een smalle donkere balk boven het werkvlak.
 *
 * De breedtes worden gemeten op de *container* en niet op het venster, want
 * dezelfde schil wordt ook ingebed getoond. Dat doet `BreakpointProvider`.
 *
 * Een werkplek met een eigen donkere balk, zoals de opname, neemt de knop
 * "Menu" over (`useShellTopBar`): dan staan er op een iPad staand geen twee
 * balken boven elkaar.
 *
 * Hier hangt ook de rondleiding bij het eerste bezoek (`features/tour`). Bij een
 * smal scherm wijst die naar de knop "Menu" (`data-tour="menu"`).
 */
export function AppShell() {
    return (
        <BreakpointProvider>
            <RailCountsProvider>
                <ShellFrame />
            </RailCountsProvider>
        </BreakpointProvider>
    );
}

function ShellFrame() {
    const { level, isNarrow } = useBreakpoint();
    const location = useLocation();
    const [railOpen, setRailOpen] = useState(false);

    /*
     * De lade gaat dicht zodra je ergens heen navigeert - ook met de terugknop
     * of via een redirect van een guard - en zodra het venster breed genoeg is
     * om de rail weer vast te zetten.
     *
     * Dit tijdens de render en niet in een effect: React rendert dan meteen
     * opnieuw zonder de open lade eerst te tonen.
     */
    const openFor = `${location.pathname}|${isNarrow}`;
    const [lastOpenFor, setLastOpenFor] = useState(openFor);
    const [topBarClaims, setTopBarClaims] = useState(0);

    if (lastOpenFor !== openFor) {
        setLastOpenFor(openFor);

        if (railOpen) {
            setRailOpen(false);
        }
    }

    const claim = useCallback(() => {
        setTopBarClaims((count) => count + 1);

        return () => setTopBarClaims((count) => count - 1);
    }, []);
    const toggleRail = useCallback(() => setRailOpen((open) => !open), []);
    const topBar = useMemo<ShellTopBarContextValue>(
        () => ({
            menu: isNarrow ? { open: railOpen, toggle: toggleRail, controls: DRAWER_ID } : null,
            claim,
        }),
        [isNarrow, railOpen, toggleRail, claim],
    );

    return (
        <div data-breakpoint={level} className="flex h-full flex-col bg-[var(--rail-bg)]">
            {/* Meekijken als iemand anders reist mee naar elk scherm, niet alleen
                naar het platformbeheer. Daarom hangt de balk hier en niet daar. */}
            <ImpersonationBar />

            <div className="flex min-h-0 flex-1 overflow-hidden">
                {isNarrow ? (
                    <Drawer
                        id={DRAWER_ID}
                        open={railOpen}
                        onClose={() => setRailOpen(false)}
                        position="fixed"
                        side="left"
                        width={246}
                        elevation="rail"
                        surface="dark"
                        title="Hoofdnavigatie"
                    >
                        <NavRail variant="drawer" onNavigate={() => setRailOpen(false)} />
                    </Drawer>
                ) : (
                    <NavRail />
                )}

                <main className="flex min-w-0 flex-1 flex-col bg-[var(--surface-page)]">
                    {isNarrow && topBarClaims === 0 ? (
                        <NarrowTopBar open={railOpen} onToggle={toggleRail} />
                    ) : null}

                    <SampleDataNotice />

                    {/*
                     * Eén grens per scherm: loopt een scherm vast, dan blijft de
                     * rail staan en zie je nog steeds in welke omgeving je zit.
                     * De route is de resetsleutel, dus wegnavigeren is naast de
                     * knop de tweede uitweg.
                     */}
                    <ErrorBoundary scope={location.pathname} resetKeys={[location.pathname]}>
                        <ShellTopBarContext.Provider value={topBar}>
                            <Outlet />
                        </ShellTopBarContext.Provider>
                    </ErrorBoundary>
                </main>
            </div>

            {/* De rondleiding bij het eerste bezoek wijst plekken in deze schil aan. */}
            <FirstRunTour />
        </div>
    );
}

/** Alleen bij narrow: de balk met "Menu" boven het werkvlak. */
function NarrowTopBar({ open, onToggle }: { open: boolean; onToggle: () => void }) {
    return (
        <div className="flex shrink-0 items-center gap-3 border-b border-[var(--rail-border)] bg-[var(--rail-bg)] px-[14px] py-[9px]">
            <Button
                variant="onDarkSolid"
                size="compact"
                icon="menu"
                data-tour="menu"
                aria-expanded={open}
                aria-controls={DRAWER_ID}
                onClick={onToggle}
            >
                Menu
            </Button>

            {/* De rail is hier ingeklapt achter "Menu": alleen het beeldmerk. */}
            <RailBrand collapsed />
        </div>
    );
}
